"use client";
import styles from './styles.module.scss';
import BlogCard from './BlogCard';
import { useBlogSearch } from '@/providers/blogSearchProvider';
import { useEffect, useState } from 'react';

const sortDataByPublishDate = (data) => {
    return data.sort((a, b) => {
        const dateA = a?.node?.blogMetaFields?.publishDate ? new Date(a.node.blogMetaFields.publishDate) : new Date(0);
        const dateB = b?.node?.blogMetaFields?.publishDate ? new Date(b.node.blogMetaFields.publishDate) : new Date(0);
        return dateB - dateA;
    });
};

// Utility function to filter and chunk array
const filterAndChunkArray = (array, filterArray, query, chunkSize) => {
    if (!Array.isArray(array) || !Array.isArray(filterArray) || typeof query !== 'string') return [];

    // Filter the array based on the filterArray criteria and the query
    const filteredData = array.filter(item => 
        filterArray.some(str => item.node.uri.includes(str)) &&
        item?.node?.blogMetaFields?.title.toLowerCase().includes(query.toLowerCase())
    );

    // Chunk the filtered array
    const result = [];
    for (let i = 0; i < filteredData.length; i += chunkSize) {
        result.push(filteredData.slice(i, i + chunkSize));
    }
    return result;
};

const PaginationButton = ({ page, onClick, isActive, ariaLabel }) => (
    <button
        aria-label={ariaLabel}
        onClick={() => onClick(page)}
        className={`${styles.searchResults__paginationButton} ${isActive ? `active_button`: ''}`}
    >
        {page}
    </button>
);

const NavigationButton = ({ direction, onClick }) => (
    <button
        aria-label={`Go to ${direction} page`}
        onClick={onClick}
        className={`${styles.searchResults__paginationButton}`}
    >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d={`M${direction === 'Before' ? '20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z' : '12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z'}`} fill="#E8236B"/>
        </svg>
    </button>
);

const SearchResults = () => {
    const { searchResults, filters, loading, query } = useBlogSearch();
    const [filteredResults, setFilteredResults] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (!searchResults || !filters) return;
        const dateSortedResults  = sortDataByPublishDate(searchResults);
        const chunkedResults = filterAndChunkArray(dateSortedResults, filters, query, 9);

        setFilteredResults(chunkedResults);
        setTotalPages(chunkedResults.length || 1);
        setCurrentPage(0); // Reset to the first page whenever filters change
    }, [searchResults, query,filters]);

    const handlePageClick = (page) => {
        setCurrentPage(page - 1);
    };

    const handlePaginationClick = (direction) => {
        if (direction === 'after' && currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        } else if (direction === 'before' && currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const renderResultsPage = () => {
        const data = filteredResults[currentPage];
        if (loading) return <div>Loading...</div>;
        if (!data || data.length === 0) return <div>No results found.</div>;

        return (
            <div className={styles.searchResults__grid}>
                {data.map((result, index) => (
                    <BlogCard key={index} {...result.node} />
                ))}
            </div>
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage + 1 - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const visiblePageNumbers = pageNumbers.slice(startPage - 1, endPage);

        return (
            <div className={styles.searchResults__pagination}>
                {currentPage > 0 && (
                    <NavigationButton direction="Before" onClick={() => handlePaginationClick('before')} />
                )}
                {startPage > 1 && (
                    <>
                        <PaginationButton page={1} onClick={handlePageClick} isActive={currentPage === 0} ariaLabel="Go to page 1" />
                        <span className={styles.searchResults__ellipsis}>...</span>
                    </>
                )}
                {visiblePageNumbers.map(page => (
                    <PaginationButton
                        key={page}
                        page={page}
                        onClick={handlePageClick}
                        isActive={currentPage + 1 === page}
                        ariaLabel={`Go to page ${page}`}
                    />
                ))}
                {endPage < totalPages && (
                    <>
                        <span className={styles.searchResults__ellipsis}>...</span>
                        <PaginationButton page={totalPages} onClick={handlePageClick} isActive={currentPage + 1 === totalPages} ariaLabel={`Go to page ${totalPages}`} />
                    </>
                )}
                {currentPage < totalPages - 1 && (
                    <NavigationButton direction="After" onClick={() => handlePaginationClick('after')} />
                )}
            </div>
        );
    };

    return (
        <section className={styles.searchResults}>
            <div className='container'>
                {renderResultsPage()}
                {renderPagination()}
            </div>
        </section>
    );
};

export default SearchResults;
